import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { ArrowLeft, Plus, Save, Trash2, Eye, Upload, X, ImageIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL_CATEGORIES = ["PLAY", "DINING", "STAY", "WELLNESS", "GOLF", "EXPERIENCES", "SHOPPING"];
const DESIGNATIONS = ["LV_PARTNER", "FOUNDING_HOME_CLUB", "LV_GLOBAL_PARTNER"];
const SCOPES = ["LOCAL", "CHAPTER", "MULTI_MARKET", "GLOBAL"];
const STATUSES = ["ACTIVE", "INACTIVE"];

export default function PartnerEdit() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: partner, isLoading } = useQuery({ queryKey: ["admin-partner", id], queryFn: () => api.get(`/admin/partners/${id}`) });
  const { data: chapters = [] } = useQuery({ queryKey: ["chapters"], queryFn: () => api.get("/chapters") });

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newPriv, setNewPriv] = useState(null);
  const [editPriv, setEditPriv] = useState(null);

  useEffect(() => {
    if (partner && !form) {
      setForm({ name: partner.name || "", description: partner.description || "", location: partner.location || "", market_chapter_id: partner.market_chapter_id || "", designation: partner.designation || "LV_PARTNER", geographic_scope: partner.geographic_scope || "LOCAL", categories: partner.categories || [], logo_url: partner.logo_url || "", cover_image_url: partner.cover_image_url || "", status: partner.status || "ACTIVE", google_maps_url: partner.google_maps_url || "", website_url: partner.website_url || "", terms: partner.terms || "", is_featured: partner.is_featured || false });
    }
  }, [partner, form]);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-partner", id] }); qc.invalidateQueries({ queryKey: ["admin-partners"] }); };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (c) => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));

  const savePartner = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/partners/${id}`, { ...form, logo_url: form.logo_url || null, cover_image_url: form.cover_image_url || null, market_chapter_id: form.market_chapter_id || null, google_maps_url: form.google_maps_url || null, website_url: form.website_url || null, terms: form.terms || null });
      toast.success("Partner updated");
      invalidate();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const saveNewPrivilege = async () => {
    if (!newPriv?.title?.trim()) { toast.error("Title required"); return; }
    try {
      await api.post(`/admin/partners/${id}/privileges`, newPriv);
      toast.success("Privilege added");
      setNewPriv(null);
      invalidate();
    } catch (err) { toast.error(err.message); }
  };

  const saveEditPrivilege = async () => {
    if (!editPriv) return;
    try {
      await api.patch(`/admin/privileges/${editPriv.id}`, editPriv);
      toast.success("Privilege updated");
      setEditPriv(null);
      invalidate();
    } catch (err) { toast.error(err.message); }
  };

  const deactivatePrivilege = async (pvId) => {
    try {
      await api.patch(`/admin/privileges/${pvId}`, { status: "INACTIVE" });
      toast.success("Privilege deactivated");
      invalidate();
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading || !form) return <div className="py-12 text-center"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>;

  return (
    <div data-testid="partner-edit-page">
      <Link to="/admin/partners" className="inline-flex items-center gap-2 text-sm text-lv-muted hover:text-cream transition-colors mb-6 min-h-[44px]"><ArrowLeft size={16} /> Partners</Link>
      <div className="flex items-center justify-between gap-4 mb-8">
        <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide" data-testid="partner-edit-name">{partner.name}</h1>
        <Link to={`/privileges/${id}`} target="_blank" className="lv-btn-secondary inline-flex items-center gap-2 text-xs min-h-[36px] px-4 shrink-0" data-testid="preview-member-view"><Eye size={14} /> Preview</Link>
      </div>

      {/* Partner details form */}
      <div className="bg-walnut border border-lv-border p-5 sm:p-6 mb-6 space-y-4">
        <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-3">Partner Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Name</label><input value={form.name} onChange={(e) => update("name", e.target.value)} className="lv-input" data-testid="edit-partner-name" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Location</label><input value={form.location} onChange={(e) => update("location", e.target.value)} className="lv-input" /></div>
          <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Description</label><textarea value={form.description} onChange={(e) => update("description", e.target.value)} className="lv-input min-h-[60px]" rows={2} /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Designation</label>
            <Select value={form.designation} onValueChange={(v) => update("designation", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{DESIGNATIONS.map((d) => <SelectItem key={d} value={d} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{d.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Geographic Scope</label>
            <Select value={form.geographic_scope} onValueChange={(v) => update("geographic_scope", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{SCOPES.map((s) => <SelectItem key={s} value={s} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Chapter</label>
            <Select value={form.market_chapter_id} onValueChange={(v) => update("market_chapter_id", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{chapters.map((ch) => <SelectItem key={ch.id} value={ch.id} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{ch.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Status</label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Categories</label>
          <div className="flex flex-wrap gap-2">{ALL_CATEGORIES.map((c) => <button key={c} type="button" onClick={() => toggleCat(c)} className={`px-2.5 py-1 text-xs tracking-wider uppercase border min-h-[32px] transition-colors ${form.categories.includes(c) ? "bg-bronze/20 border-bronze text-bronze" : "border-lv-border text-lv-muted hover:border-bronze/40"}`}>{c}</button>)}</div>
        </div>
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Logo URL</label><input value={form.logo_url} onChange={(e) => update("logo_url", e.target.value)} className="lv-input" /></div>
        <CoverImageUpload currentUrl={form.cover_image_url} onUploaded={(url) => update("cover_image_url", url)} onRemove={() => update("cover_image_url", "")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Google Maps URL</label><input value={form.google_maps_url} onChange={(e) => update("google_maps_url", e.target.value)} className="lv-input" placeholder="https://maps.google.com/..." data-testid="edit-partner-maps-url" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Website URL</label><input value={form.website_url} onChange={(e) => update("website_url", e.target.value)} className="lv-input" placeholder="https://..." data-testid="edit-partner-website-url" /></div>
        </div>
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Terms & Conditions</label><textarea value={form.terms} onChange={(e) => update("terms", e.target.value)} className="lv-input min-h-[80px]" rows={3} placeholder="Partner-specific terms, conditions, or usage notes..." data-testid="edit-partner-terms" /></div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => update("is_featured", !form.is_featured)} className={`relative w-10 h-5 rounded-full transition-colors ${form.is_featured ? "bg-bronze" : "bg-walnut-deep border border-lv-border"}`} data-testid="edit-partner-featured-toggle">
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-cream transition-transform ${form.is_featured ? "translate-x-5" : ""}`} />
          </button>
          <label className="text-xs text-lv-muted tracking-wide">Featured Partner</label>
        </div>
        <button onClick={savePartner} disabled={saving} className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="save-partner-btn"><Save size={14} /> {saving ? "Saving\u2026" : "Save Changes"}</button>
      </div>

      {/* Privileges */}
      <div className="bg-walnut border border-lv-border p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted">Privileges</h2>
          <button onClick={() => setNewPriv({ title: "", description: "", how_to_use: "", restrictions: "", category: "", valid_from: "", valid_through: "", eligible_member_statuses: ["ACTIVE"] })} className="lv-btn-secondary inline-flex items-center gap-1.5 text-xs min-h-[36px]" data-testid="add-privilege-btn"><Plus size={14} /> Add Privilege</button>
        </div>

        <div className="space-y-4">
          {partner.privileges?.map((pv) => (
            <div key={pv.id} className={`border p-4 ${pv.status === "ACTIVE" ? "border-lv-border" : "border-lv-border/30 opacity-60"}`}>
              {editPriv?.id === pv.id ? (
                <PrivilegeForm priv={editPriv} onChange={setEditPriv} onSave={saveEditPrivilege} onCancel={() => setEditPriv(null)} />
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-cream font-medium">{pv.title}</p>
                    {pv.description && <p className="text-xs text-lv-muted mt-1">{pv.description}</p>}
                    {pv.category && <span className="text-[9px] text-lv-muted/60 uppercase tracking-wider">{pv.category}</span>}
                    <span className={`ml-2 text-[9px] uppercase tracking-wider ${pv.status === "ACTIVE" ? "text-lv-active" : "text-lv-inactive"}`}>{pv.status}</span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditPriv({ ...pv })} className="text-lv-muted hover:text-cream text-xs px-2 py-1 min-h-[32px]">Edit</button>
                    {pv.status === "ACTIVE" && <button onClick={() => deactivatePrivilege(pv.id)} className="text-lv-inactive hover:text-lv-inactive/80 text-xs px-2 py-1 min-h-[32px]"><Trash2 size={12} /></button>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {newPriv && (
          <div className="mt-4 border border-bronze/30 p-4">
            <p className="text-xs tracking-[0.2em] uppercase text-bronze mb-3">New Privilege</p>
            <PrivilegeForm priv={newPriv} onChange={setNewPriv} onSave={saveNewPrivilege} onCancel={() => setNewPriv(null)} />
          </div>
        )}
      </div>
    </div>
  );
}

function CoverImageUpload({ currentUrl, onUploaded, onRemove }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = { current: null };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const result = await api.upload("/admin/upload?category=partners", fd);
      onUploaded(result.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
  const imgSrc = currentUrl ? (currentUrl.startsWith("http") ? currentUrl : `${API_BASE}${currentUrl}`) : null;

  return (
    <div data-testid="cover-image-upload">
      <label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Cover Image</label>
      {imgSrc ? (
        <div className="relative group">
          <img src={imgSrc} alt="Cover" className="w-full h-40 object-cover border border-lv-border" data-testid="cover-image-preview" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <label className="lv-btn-secondary text-xs min-h-[36px] px-4 cursor-pointer inline-flex items-center gap-1.5">
              <Upload size={14} /> Replace
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFile} className="hidden" data-testid="cover-image-file-input" />
            </label>
            <button onClick={onRemove} className="lv-btn-secondary text-xs min-h-[36px] px-4 inline-flex items-center gap-1.5" data-testid="cover-image-remove"><X size={14} /> Remove</button>
          </div>
          {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><div className="w-10 h-0.5 bg-bronze animate-pulse" /></div>}
        </div>
      ) : (
        <label className={`flex flex-col items-center justify-center h-40 border border-dashed border-lv-border hover:border-bronze/40 transition-colors cursor-pointer ${uploading ? "pointer-events-none opacity-60" : ""}`} data-testid="cover-image-dropzone">
          {uploading ? (
            <div className="w-10 h-0.5 bg-bronze animate-pulse" />
          ) : (
            <>
              <ImageIcon size={24} className="text-lv-muted mb-2" />
              <span className="text-xs text-lv-muted tracking-wide">Click to upload cover image</span>
              <span className="text-[10px] text-lv-muted/50 mt-1">JPG, PNG, WebP, GIF — max 10 MB</span>
            </>
          )}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFile} className="hidden" data-testid="cover-image-file-input-empty" />
        </label>
      )}
    </div>
  );
}

function PrivilegeForm({ priv, onChange, onSave, onCancel }) {
  const u = (k, v) => onChange({ ...priv, [k]: v });
  return (
    <div className="space-y-3">
      <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Title *</label><input value={priv.title || ""} onChange={(e) => u("title", e.target.value)} className="lv-input" data-testid="privilege-title" /></div>
      <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Description</label><textarea value={priv.description || ""} onChange={(e) => u("description", e.target.value)} className="lv-input min-h-[60px]" rows={2} /></div>
      <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">How to Use</label><textarea value={priv.how_to_use || ""} onChange={(e) => u("how_to_use", e.target.value)} className="lv-input min-h-[60px]" rows={2} /></div>
      <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Restrictions / Conditions</label><input value={priv.restrictions || ""} onChange={(e) => u("restrictions", e.target.value)} className="lv-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Valid From</label><input type="date" value={priv.valid_from || ""} onChange={(e) => u("valid_from", e.target.value)} className="lv-input" /></div>
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Valid Through</label><input type="date" value={priv.valid_through || ""} onChange={(e) => u("valid_through", e.target.value)} className="lv-input" /></div>
      </div>
      <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Category</label><input value={priv.category || ""} onChange={(e) => u("category", e.target.value)} className="lv-input" placeholder="e.g. PLAY, DINING" /></div>
      <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Sort Order</label><input type="number" value={priv.sort_order ?? 0} onChange={(e) => u("sort_order", parseInt(e.target.value) || 0)} className="lv-input" placeholder="0" data-testid="privilege-sort-order" /></div>
      <div className="flex gap-2 pt-1">
        <button onClick={onSave} className="lv-btn-primary text-xs min-h-[36px] px-4">Save</button>
        <button onClick={onCancel} className="lv-btn-secondary text-xs min-h-[36px] px-4">Cancel</button>
      </div>
    </div>
  );
}
